import { apiClient } from '@/lib/api'
import type {
  StandardResponse,
  MessageResponse,
} from '@/types/api-response'
import { extractData } from '@/types/api-response'
import type {
  PricingTierListDto,
  PricingTierDetailDto,
  CreatePricingTierRequest,
  UpdatePricingTierRequest,
} from '@/types/pricing-tier'

/**
 * Admin Pricing Tier Service
 *
 * Handles all pricing tier management API calls for the admin panel.
 * All responses follow StandardResponse<T> or PaginatedStandardResponse<T> format.
 */
export const adminPricingTierService = {
  /**
   * Get list of pricing tiers for a component price
   *
   * Returns all pricing tiers for a specific component price.
   * Note: Backend returns a list, not paginated response.
   *
   * @param componentPriceId - Component price ID (UUID)
   */
  async getTiers(componentPriceId: string): Promise<PricingTierListDto[]> {
    const response = await apiClient.get<StandardResponse<PricingTierListDto[]>>(
      `/api/v1/admin/pricing/prices/${componentPriceId}/tiers`
    )

    // Interceptor already handles error responses, so response.data is guaranteed to be success
    return extractData(response.data)
  },

  /**
   * Create new pricing tier
   *
   * Returns created pricing tier details with 201 Created status.
   *
   * @param componentPriceId - Component price ID (UUID)
   * @param data - Pricing tier creation data
   */
  async createTier(
    componentPriceId: string,
    data: Omit<CreatePricingTierRequest, 'componentPriceId'>
  ): Promise<PricingTierDetailDto> {
    const response = await apiClient.post<StandardResponse<PricingTierDetailDto>>(
      `/api/v1/admin/pricing/prices/${componentPriceId}/tiers`,
      data
    )

    // Interceptor already handles error responses, so response.data is guaranteed to be success
    return extractData(response.data)
  },

  /**
   * Update pricing tier details
   *
   * Returns success message (200 OK).
   *
   * @param tierId - Pricing tier ID (UUID)
   * @param data - Pricing tier update data
   */
  async updateTier(tierId: string, data: UpdatePricingTierRequest): Promise<void> {
    await apiClient.put<MessageResponse>(`/api/v1/admin/pricing/tiers/${tierId}`, data)

    // Interceptor already handles error responses
  },

  /**
   * Delete pricing tier
   *
   * Returns success message (200 OK).
   * Note: This permanently deletes the pricing tier.
   *
   * @param tierId - Pricing tier ID (UUID)
   */
  async deleteTier(tierId: string): Promise<void> {
    await apiClient.delete<MessageResponse>(`/api/v1/admin/pricing/tiers/${tierId}`)

    // Interceptor already handles error responses
  },
}
